import React, { useState, useEffect, useRef } from 'react';

/** Merge API options with placeholder rows for selected ids missing from the list (inactive users, pagination gaps). */
export function buildCaregiverOptionsWithStubs(options, selectedIds) {
  const list = Array.isArray(options) ? options : [];
  const idSet = new Set(list.map((o) => String(o.id)));
  const out = [...list];
  for (const sid of selectedIds || []) {
    const s = String(sid).trim();
    if (!s || idSet.has(s)) continue;
    const n = Number(s);
    out.push({
      id: Number.isFinite(n) && !Number.isNaN(n) ? n : s,
      first_name: '',
      last_name: ''
    });
    idSet.add(s);
  }
  return out;
}

export function personPickerLabel(row) {
  if (!row) return '';
  const a = [row.first_name, row.last_name].filter(Boolean).join(' ').trim();
  if (a) return a;
  if (row.full_name) return row.full_name;
  return row.id != null ? `ID ${row.id}` : '';
}

export function PatientAscStyleSinglePicker({
  label,
  name,
  required,
  value,
  options,
  error,
  emptyMessage,
  placeholder,
  onPick,
  id
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const selected = options.find((o) => String(o.id) === String(value));
  const display = selected ? personPickerLabel(selected) : '';

  return (
    <div className="add-patient-form-group add-patient-asc-pick-root" ref={rootRef} id={id}>
      <label>
        {label}
        {required ? (
          <>
            {' '}
            <span className="add-patient-required">*</span>
          </>
        ) : null}
      </label>
      <div className="add-patient-asc-pick-shell">
        <div
          className={`add-patient-asc-pick-trigger ${error ? 'is-invalid' : ''} ${open ? 'is-open' : ''}`}
          role="button"
          tabIndex={0}
          onClick={() => setOpen((o) => !o)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setOpen((o) => !o);
            }
          }}
        >
          {!value || !selected ? (
            <span className="add-patient-asc-pick-placeholder">{placeholder}</span>
          ) : (
            <span className="add-patient-asc-pick-selected-text">{display}</span>
          )}
        </div>
        {open ? (
          <ul className="add-patient-asc-pick-list" role="listbox">
            {options.length === 0 ? (
              <li className="add-patient-asc-pick-empty">{emptyMessage}</li>
            ) : (
              options.map((opt) => (
                <li
                  key={opt.id}
                  role="option"
                  aria-selected={String(opt.id) === String(value)}
                  className={`add-patient-asc-pick-option ${String(opt.id) === String(value) ? 'is-selected' : ''}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    onPick(name, String(opt.id));
                    setOpen(false);
                  }}
                >
                  {personPickerLabel(opt)}
                </li>
              ))
            )}
          </ul>
        ) : null}
      </div>
      {error ? <div className="add-patient-error-message">{error}</div> : null}
    </div>
  );
}

export function PatientAscStyleMultiPicker({
  label,
  required,
  selectedIds,
  options,
  onAddId,
  onRemoveId,
  error,
  emptyMessage,
  placeholder,
  id
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const sel = (selectedIds || []).map((x) => String(x));
  const available = options.filter((o) => !sel.includes(String(o.id)));

  return (
    <div className="add-patient-form-group add-patient-asc-pick-root" ref={rootRef} id={id}>
      <label>
        {label}
        {required ? (
          <>
            {' '}
            <span className="add-patient-required">*</span>
          </>
        ) : null}
      </label>
      <div className="add-patient-asc-pick-shell">
        <div
          className={`add-patient-asc-pick-trigger add-patient-asc-pick-trigger--multi ${error ? 'is-invalid' : ''} ${open ? 'is-open' : ''}`}
          role="button"
          tabIndex={0}
          onClick={() => setOpen((o) => !o)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setOpen((o) => !o);
            }
          }}
        >
          {sel.length === 0 ? (
            <span className="add-patient-asc-pick-placeholder">{placeholder}</span>
          ) : (
            sel.map((idStr) => {
              const row = options.find((o) => String(o.id) === String(idStr));
              const chipText = row ? personPickerLabel(row) : `ID ${idStr}`;
              return (
                <span key={idStr} className="add-patient-asc-pick-chip">
                  <span className="add-patient-asc-pick-chip-text">{chipText}</span>
                  <button
                    type="button"
                    className="add-patient-asc-pick-chip-remove"
                    aria-label={`Remove ${chipText}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveId(idStr);
                    }}
                  >
                    ×
                  </button>
                </span>
              );
            })
          )}
        </div>
        {open ? (
          <ul className="add-patient-asc-pick-list" role="listbox">
            {options.length === 0 ? (
              <li className="add-patient-asc-pick-empty">{emptyMessage}</li>
            ) : available.length === 0 ? (
              <li className="add-patient-asc-pick-empty">All selected</li>
            ) : (
              available.map((opt) => (
                <li
                  key={opt.id}
                  role="option"
                  aria-selected={false}
                  className="add-patient-asc-pick-option"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    onAddId(String(opt.id));
                    setOpen(false);
                  }}
                >
                  {personPickerLabel(opt)}
                </li>
              ))
            )}
          </ul>
        ) : null}
      </div>
      {error ? <div className="add-patient-error-message">{error}</div> : null}
    </div>
  );
}
